/**
 * Swym Wishlist Plus hearts on Nosto product cards.
 *
 * Swym's "Wishlist Collections" feature injects a heart button into every native
 * <product-card> once, on page load, by scanning the DOM for cards under
 * `product-list.product-list`
 * (swat.collectionsApi.collectionsConfigObject[schema].selectors).
 *
 * Every Nosto placement on this store renders the theme's card markup asynchronously
 * via <nosto-dynamic-card section="product-card">: the search SERP / category grid /
 * autocomplete (the Nosto search-templates bundle), the recommendation carousel
 * (nosto/product-carousel.html), the static product grid (nosto/product-grid.html) and
 * any future placement. Those cards are added AFTER Swym's initial scan, so they never
 * receive a heart. This module is the single, page-global place that fixes all of them.
 *
 * Fix: teach Swym's scanner about Nosto's card structure (one extra selector), then
 * re-run its injection whenever a dynamic card finishes rendering. The web component
 * dispatches a bubbling `@nosto/DynamicCard/loaded` event after it writes its markup
 * (available since @nosto/web-components v11 — the "no stable loaded event" note in
 * _nosto-carousel.js predates it). That is the precise, cheap signal; far better than a
 * document-wide MutationObserver that would re-scan on every unrelated DOM change.
 *
 * initializeCollections is idempotent — it skips any card that already has a
 * `.swym-wishlist-collections-v2-container` — so repeated calls only add hearts to new
 * cards. Everything is null-guarded: if the Swym app is absent (e.g. cookie consent not
 * yet granted, or its internals change) this simply no-ops.
 */

import { anyEnabled } from './_nosto-flags.js';

// Matches Nosto's inner <product-card> tiles wherever they render, without matching the
// theme's own native cards (which have no <nosto-dynamic-card> ancestor).
const NOSTO_SELECTOR = {
  AttachButtonSelector: 'nosto-dynamic-card product-card.product-card',
  ProductUrlSelector: 'product-card.product-card .product-card__figure a.product-card__media',
};

function registerSwymReinjection() {
  const schema = window.Shopify && window.Shopify.theme && window.Shopify.theme.schema_name;

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function (swat) {
    const api = swat && swat.collectionsApi;
    if (!api || typeof api.initializeCollections !== 'function') return;

    // initializeCollections rebuilds its working selector list from the schema config on
    // every call, so register Nosto's selector on that source array (idempotently).
    function ensureSelector() {
      const cfg = schema && api.collectionsConfigObject && api.collectionsConfigObject[schema];
      const list = cfg && cfg.selectors;
      if (
        list &&
        !list.some(function (s) {
          return s.AttachButtonSelector === NOSTO_SELECTOR.AttachButtonSelector;
        })
      ) {
        list.push(NOSTO_SELECTOR);
      }
    }

    let scheduled = false;
    function inject() {
      scheduled = false;
      ensureSelector();
      try {
        api.initializeCollections(swat, true, schema);
      } catch (e) {
        // Swym internals changed — leave native cards untouched rather than throw.
      }
    }
    // A grid/carousel resolves a burst of cards at once; one full-page scan covers them.
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        setTimeout(inject, 0);
      });
    }

    ensureSelector();
    document.addEventListener('@nosto/DynamicCard/loaded', schedule);
    schedule(); // pick up any cards that rendered before Swym finished loading
  });
}

/**
 * In the recommendation carousel and static product grid templates the whole card is
 * wrapped in <a class="product-link">, so Swym's heart button lands inside that anchor.
 * Swym stopPropagation()s its own click but never preventDefault()s, so the click would
 * still trigger the anchor's default navigation. Cancel it in the capture phase for
 * hearts that sit inside a link — a no-op for search / native cards, where the heart is
 * a sibling of the media link rather than a descendant of a full-card anchor.
 */
function preventHeartClickNavigation() {
  document.addEventListener(
    'click',
    function (e) {
      const target = e.target;
      const btn = target && target.closest && target.closest('.swym-wishlist-collections-v2');
      if (btn && btn.closest('a[href]')) e.preventDefault();
    },
    true
  );
}

function start() {
  registerSwymReinjection();
  preventHeartClickNavigation();
}

// Both feature sets render <nosto-dynamic-card>, so either one needs the hearts. Off
// when both are off — there are no dynamic cards to decorate. See _nosto-flags.js.
if (anyEnabled()) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}
